import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import './App.css';
import { onDataChanged, onScreenResumed } from './utils/dataChangeSignal';
import { onIstHours } from './utils/dayBoundary';
import InventoryOverview from './components/InventoryOverview';
import Xyz from './components/xyz';
import Dashboard from './components/Dashboard';
import Leakage from './components/Leakage';
import LeakageEom from './components/leakage_eom';
import SalesEom from './components/sales_eom';
import WastageEom from './components/wastage_eom';
import ExpenseEom from './components/expense_eom';
import LoginScreen from './components/LoginScreen';
import SignUpScreen from './components/SignUpScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import EmailVerificationScreen from './components/EmailVerificationScreen';
import OnboardingPage from './components/OnboardingPage';
import Notification from "./components/notifications";
// Deprecated unit mapping onboarding screens
// import MapStep1 from './components/map';
// import KitchenUnitsStep from './components/map1';
// import SupplierConversions from './components/map2';
// import UnitMappingComplete from './components/map3';
import RecipeListScreen from './components/itemmap';
import IngredientReconciliationRegistry from './components/IngredientReconciliationRegistry';
import BillMemoryPage from './components/BillMemoryPage';
import SalesNameCheck from './components/SalesNameCheck';
import { getStatus as getSalesNameCheckStatus } from './components/salesNameCheck/salesNameCheckApi';
import { salesNameCheckRefusal } from './utils/salesNameCheckRefusal';
import { shouldAdoptOnboardedUser } from './utils/onboardedSync';
import StructuralActionLog from './components/StructuralActionLog';
import QRitemmap from './components/QRBILLING/QRitemmap';
import ReportsScreen from './components/reportscreen';
import SalesReport from './components/SalesReport';
import OnlineOrdersReport from './components/OnlineOrdersReport';
import TestUnitMapping from './components/TestUnitMapping';
import StockInForm from './components/StockInForm';
import StockOutForm from './components/StockOutForm';
import Usage from './components/Usage';
import OCRUpload from './components/OCRUpload';
import VendorManagement from './components/VendorManagement';
import TodaysSalesReport from './components/TodaysSalesReport';
import Navigation from './components/Navigation';
import FooterNav from './components/FooterNav';
import SettingsPage from './components/SettingsPage';
import CategoryMaintenancePage from './components/CategoryMaintenancePage';
import ClosingStockPage from './components/ClosingStockPage';
import ServiceTester from './components/ServiceTester';
import { checkDatabaseStatus, logStartupInfo } from './services/dbStatusService';
import MinimalStock from './components/MinimalStock';
import Verification from './components/Verification ';
import RecipeBookCatalog from './components/RecipeBookCatalog';
import RecipeLibraryReview from './components/RecipeLibraryReview';
// Structured Recipe Book — the recipe book rendered on screen instead of straight to a
// file. Owner-only and feature-flagged on the server; the screen simply asks and shows
// the right state, so an unflagged build never reaches it from the nav.
import StructuredRecipeBook from './components/RecipeBook/StructuredRecipeBook';
import ClosingStockCheck from './components/ClosingStockCheck';
import CreateReorder from './components/CreateReorder';
import InventoryCalculator from './components/InventoryCalculator';
import WastageThresholds from './components/WastageThresholds';
import WastageThresholdApprovals from './components/WastageThresholdApprovals';
import TestApprovalPopup from './components/TestApprovalPopup';
import ExpiryThresholds from './components/ExpiryThresholds';
import ExpiryThresholdsComponent from './components/ExpiryThresholds';
import QRBillingOnboardingComplete from './components/QRBILLING/QRBillingOnboardingComplete';
import OwnerApp from './components/QRBILLING/OwnerApp';
import OwnerDashboard from './components/QRBILLING/OwnerDashboard';
import OwnerAnalytics from './components/QRBILLING/OwnerAnalytics';
import OwnerMenuManagement from './components/QRBILLING/OwnerMenuManagement';
import QRTableManagement from './components/QRBILLING/QRTableManagement';
import ChefPage from './components/QRBILLING/ChefPage';
import WaiterDashboard from './components/QRBILLING/WaiterDashboard';
import InitialItemRecoveryPage from './components/InitialItemRecoveryPage';
import OnboardingTSRWrapper from './components/OnboardingTSRWrapper';
import AuthContext from './contexts/AuthContext';
import { TranslationProvider } from './translations/useTranslate';
import TestMediumFeatures from './pages/TestMediumFeatures';
import CustomizationMapping from './components/CustomizationMapping';
// FEATURE DISABLED: VID (Virtual Inventory Deduction) - To re-enable, uncomment below
// import VirtualInventoryManager from './components/VirtualInventoryManager';
import ExpiryControlPanel from './components/ExpiryControlPanel';
import ProtectedRoute from './components/ProtectedRoute';
import { getScreenPermissions } from './config/screenPermissions';
import ComplimentaryRecipeFinalization from './components/ComplimentaryRecipeFinalization';
import { TermsPage, PrivacyPolicyPage, RefundPolicyPage } from './components/PolicyPages';
import { scrollToTop } from './utils/scroll';
import { isLocalDevHost } from './utils/localDevHost';
import OnboardingSuccessScreen from './components/OnboardingSuccessScreen';
import TSRLockModal from './components/TSRLockModal';
import SubscriptionLockModal from './components/SubscriptionLockModal';
import SubscriptionEndingBanner from './components/SubscriptionEndingBanner';
import LegacyUnitWarning from './components/LegacyUnitWarning';

// Monthly leakage (item-first, snapshot-backed). Lazy: four screens plus their
// dialogs are dead weight in the main bundle for every user who never opens
// Reports.
const LeakageMonth = React.lazy(() => import('./components/leakageMonthly/LeakageMonth'));
const LeakageCategory = React.lazy(() => import('./components/leakageMonthly/LeakageCategory'));
const LeakageItemCard = React.lazy(() => import('./components/leakageMonthly/LeakageItemCard'));
const LeakageHistory = React.lazy(() => import('./components/leakageMonthly/LeakageHistory'));
// Phase 0 (data truth) and the weekly A-tier count sheet. Lazy for the same
// reason as the four report screens: nobody opens them on the way to anything.
const PrepYieldReview = React.lazy(() => import('./components/leakageMonthly/PrepYieldReview'));
const MappingIntegrity = React.lazy(() => import('./components/leakageMonthly/MappingIntegrity'));
const ATierCount = React.lazy(() => import('./components/leakageMonthly/ATierCount'));
const BanquetOrdersReport = React.lazy(() => import('./components/banquet/BanquetOrdersReport'));
const BanquetEom = React.lazy(() => import('./components/banquet/BanquetEom'));
// Net Profit Analyzer — Payroll & Fixed Expenses on the server ledger (replaces the iframe demo
// and the browser-only Dashboard/Close screens).
const NetProfitApp = React.lazy(() => import('./components/netProfit/NetProfitApp'));

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL;
const SESSION_SCREEN_STATE_KEY = 'invexis:last_screen_state';
const HISTORY_ROUTE_STATE_KEY = 'invexis:route_state';
const RAPID_REFRESH_GUARD_KEY = 'invexis:rapid_refresh_guard';
const RAPID_REFRESH_WINDOW_MS = 10000;
const RAPID_REFRESH_LIMIT = 2;
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === 'production';
const LOCAL_ONLY_REPORT_SCREENS = new Set([]);
const isLocalOnlyReportScreen = (screenName) => LOCAL_ONLY_REPORT_SCREENS.has(screenName);

// Businesses exempt from the subscription lock. These never see the
// "Subscription Inactive" / "Renew Payment" screen, regardless of the
// subscription flag or trial expiry on their user record.
// 165 = Orso cafe (orsocafe87@gmail.com)
const SUBSCRIPTION_LOCK_EXEMPT_BUSINESS_IDS = new Set([165]);
const isSubscriptionLockExemptBusiness = (businessId) => {
  const numericBusinessId = Number(businessId);
  return Number.isFinite(numericBusinessId)
    && SUBSCRIPTION_LOCK_EXEMPT_BUSINESS_IDS.has(numericBusinessId);
};

const NON_PERSISTABLE_SCREENS = new Set([
  'login',
  'signup',
  'forgot',
  'reset-password',
  'email-verification',
  'terms',
  'privacy',
  'refund',
  // Onboarding-only screens. Restoring one of these into a business that has
  // since gone live drops the owner back into a setup flow that is over —
  // there is no way out of 'onboarding-success' or 'closing-stock-check' for
  // an onboarded account. Screens that onboarding SHARES with normal use
  // ('itemmap', 'inventory', 'todays-sales-report') stay persistable, because
  // for a live user they are ordinary destinations.
  'onboarding',
  'qr-billing-onboarding',
  'onboarding-success',
  'initial-recovery',
  'verification',
  'sales-name-check',
  'ingredient-reconciliation',
  'closing-stock-check'
]);

/**
 * Forget where the user was.
 *
 * Called when onboarding completes: the last screen of setup is very often one
 * of the shared ones (the TSR screen especially), and restoring it on the next
 * load would land a freshly-onboarded owner back in the flow they just
 * finished instead of on their dashboard.
 */
const clearPersistedRouteState = () => {
  try {
    sessionStorage.removeItem(SESSION_SCREEN_STATE_KEY);
  } catch (error) {
    console.warn('Failed to clear stored route state:', error);
  }
  try {
    const currentHistoryState = (window.history && typeof window.history.state === 'object' && window.history.state)
      ? window.history.state
      : {};
    if (currentHistoryState[HISTORY_ROUTE_STATE_KEY]) {
      const { [HISTORY_ROUTE_STATE_KEY]: _hiddenRouteState, ...rest } = currentHistoryState;
      window.history.replaceState(rest, document.title, window.location.href);
    }
  } catch (error) {
    console.warn('Failed to clear hidden route state from history:', error);
  }
};

function App() {
  // Helper to determine initial screen based on URL to prevent flashes
  const getInitialScreen = () => {
    const path = window.location.pathname;
    if (path === '/Terms&Conditions' || path === '/terms-conditions') return 'terms';
    if (path === '/privacy_policy') return 'privacy';
    if (path === '/refund_policy') return 'refund';
    
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Check if email verification based on path (handle trailing slash from CDN)
      const normalizedPath = path.replace(/\/$/, '');
      if (normalizedPath === '/verify-email') return 'email-verification';
      // Otherwise password reset
      return 'reset-password';
    }
    
    return 'login';
  };

  // Track previous screen for conditional back button rendering
  const [previousScreen, setPreviousScreen] = useState(null);
  const [screenParams, setScreenParams] = useState({});
  // Which Item Map opened Sales Name Check, so its Back returns to the same one.
  const [salesNameCheckBackTo, setSalesNameCheckBackTo] = useState('itemmap');
  const localOnlyReportsEnabled = !IS_PRODUCTION_BUILD;
  const trackedSetScreen = (next, params = {}) => {
    const blockedInProduction = IS_PRODUCTION_BUILD && isLocalOnlyReportScreen(next);
    const targetScreen = blockedInProduction ? 'dashboard' : next;
    setPreviousScreen(screen); // screen is current before update
    setScreen(targetScreen);
    setScreenParams(blockedInProduction ? {} : params); // Store navigation parameters
  };
  const [screen, setScreen] = useState(getInitialScreen());
  const { user, isAuthenticated, setUser } = useContext(AuthContext);
  const [tsrLockActive, setTsrLockActive] = useState(false);
  const [tsrUnlockAnimating, setTsrUnlockAnimating] = useState(false);
  const [yesterdayDate, setYesterdayDate] = useState(null);
  const [yesterdayWastageRecorded, setYesterdayWastageRecorded] = useState(false);
  // The date the TSR screen opens on. Separate from yesterdayDate on purpose:
  // that one is the LOCK's subject and is null until the 26h grace expires, so
  // reusing it as the filing date defaulted every 00:00–02:00 submission to the
  // wrong day.
  const [suggestedTsrDate, setSuggestedTsrDate] = useState(null);
  const [openWastageReasonModal, setOpenWastageReasonModal] = useState(false);
  const wasTsrLockActiveRef = useRef(false);
  // The rapid-refresh guard's answer for THIS page load (null = not asked yet).
  const rapidRefreshDecisionRef = useRef(null);

  // Disable browser scroll restoration (back/forward + SPA screen swaps).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Native Android: keep app content below system status bar (no overlap with time/battery area)
  useEffect(() => {
    const configureStatusBar = async () => {
      if (!Capacitor.isNativePlatform()) return;
      try {
        await StatusBar.setOverlaysWebView({ overlay: true });
        await StatusBar.setStyle({ style: StatusBarStyle.Dark });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        await StatusBar.hide();
      } catch (error) {
        console.warn('StatusBar configuration failed:', error);
      }
    };

    configureStatusBar();
  }, []);
  // Track password reset token from URL
  const [resetToken, setResetToken] = useState(null);
  // Track submission lock state for disabling footer during Usage countdown
  const [isFooterLocked, setIsFooterLocked] = useState(false);
  // Track where the unit mapping flow was opened from: 'onboarding' | 'settings' | null
  const [mappingOrigin, setMappingOrigin] = useState(null);
  // Track where the vendor management was opened from: 'settings' | null
  const [vendorOrigin, setVendorOrigin] = useState(null);
  // Track onboarding step (1: business details, 2: itemmap, 3: stock-in, 4: TSR)
  const [onboardingStep, setOnboardingStep] = useState(0);
  // Track if user is coming from QR billing onboarding flow
  const [fromOnboarding, setFromOnboarding] = useState(false);
  // Where to go after showing the onboarding success screen
  const [onboardingSuccessTarget, setOnboardingSuccessTarget] = useState(null);
  // Track hash-based routes (for customer menu)
  const [hashRoute, setHashRoute] = useState(null);
  // Track if QR onboarding is needed
  const [qrOnboardingRequired, setQrOnboardingRequired] = useState(false);
  // Vendor selection state for CreateReorder workflow
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isSelectingVendor, setIsSelectingVendor] = useState(false);
  // Track selected vintage recipe book for review
  const [selectedRecipeBook, setSelectedRecipeBook] = useState(null);
  // Track whether onboarding is in progress to hide global chrome like footer
  const isAuthFlow = screen === 'login' || screen === 'signup' || screen === 'forgot' || screen === 'reset-password' || screen === 'email-verification';
  const parseBooleanLike = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 't') return true;
      if (normalized === 'false' || normalized === '0' || normalized === 'f') return false;
    }
    return null;
  };
  const onboardedFlag = parseBooleanLike(user?.isOnboarded ?? user?.is_onboarded);
  const isUserInOnboardingAccount =
    onboardedFlag === false || (onboardedFlag === null && onboardingStep > 0);

  // Show footer on Item Map when accessed from Dashboard (i.e., not during onboarding)
  const isOnboarding =
    screen === 'onboarding' ||
    screen === 'qr-billing-onboarding' ||
    screen === 'onboarding-success' ||
    (screen === 'itemmap' && (fromOnboarding || isUserInOnboardingAccount || onboardingStep === 2)) ||
    (screen === 'qr-itemmap' && (fromOnboarding || isUserInOnboardingAccount || onboardingStep === 2)) ||
    // onboardingStep is 0 for a live business: "<= 3" alone made every live visit to
    // these screens an onboarding visit (footer hidden, Back into the setup flow).
    (screen === 'sales-name-check' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    (screen === 'ingredient-reconciliation' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    (screen === 'verification' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    (screen === 'inventory' && (fromOnboarding || (isUserInOnboardingAccount && onboardingStep === 3)));

  // Treat Item Map as mapping flow only during onboarding; keep other mapping screens unchanged
  const isMappingFlow =
    (screen === 'itemmap' && (fromOnboarding || isUserInOnboardingAccount || onboardingStep === 2)) ||
    (screen === 'qr-itemmap' && (fromOnboarding || isUserInOnboardingAccount || onboardingStep === 2)) ||
    (screen === 'sales-name-check' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    (screen === 'ingredient-reconciliation' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    (screen === 'verification' && (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3))) ||
    screen === 'initial-recovery' ||
    (screen === 'todays-sales-report' && onboardingStep === 4);

  // Lock footer navigation while user is in onboarding TSR -> recipe update flow.
  const isOnboardingTsrRecipeFlow =
    onboardingStep === 4 && (screen === 'itemmap' || screen === 'qr-itemmap');

  // ── Onboarding progression ─────────────────────────────────────────────────
  // Setup is four steps: 1 business details → 2 item map → 3 opening stock →
  // 4 first TSR. Every screen used to persist its own progress inline, and one
  // of them finalised the WHOLE setup from the middle of the flow — which is
  // how a business could be marked onboarded having never reached Step 4.
  //
  // There are now exactly two ways to move: advance to the next step, or
  // complete. Completion lives in one place, so it cannot happen early and it
  // cannot happen twice with two different notions of "done".

  const saveOnboardingStep = useCallback(async (step) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/auth/save-onboarding-step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ step })
      });
    } catch (error) {
      console.error('Failed to save onboarding step:', error);
    }
  }, []);

  // Persist BEFORE moving: a reload mid-navigation must resume on the step the
  // user reached, not the one they just left.
  const advanceOnboardingToStep = useCallback(async (step) => {
    await saveOnboardingStep(step);
    setOnboardingStep(step);
  }, [saveOnboardingStep]);

  // The last step reported done — mark the setup complete and hand the owner
  // their dashboard. The server clears onboarding_step in the same statement
  // that sets is_onboarded, so progress and completion can never disagree.
  //
  // `showSuccessScreen: false` is for the resume path, where the setup was
  // already finished and there is nothing to celebrate: go straight to the
  // dashboard. It also avoids a race — setting the user re-runs the init
  // effect, which routes an onboarded user to the dashboard on its own, and
  // the success screen would be torn down mid-animation.
  const completeOnboarding = useCallback(async ({ showSuccessScreen = true, target = null } = {}) => {
    const token = localStorage.getItem('token');
    let isQRUser = false;

    if (token) {
      try {
        const finalizeResponse = await fetch(`${API_URL}/auth/finalize-onboarding`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        // Spec D6: an onboarding Sales Name Check opened meanwhile (another tab or
        // device) makes the server refuse. Onboarding is NOT finished then: say
        // so and open the check, instead of showing the success screen.
        if (finalizeResponse.status === 409) {
          const refusal = salesNameCheckRefusal(409, await finalizeResponse.json().catch(() => null));
          if (refusal) {
            setFromOnboarding(true);
            setScreenParams({ mode: 'onboarding' });
            setScreen('sales-name-check');
            window.alert(refusal);
            return;
          }
        }

        // Refetch rather than trust the local copy: the QR flag decides which
        // dashboard the owner lands on.
        const userResponse = await fetch(`${API_URL}/auth/user`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          isQRUser = userData.isQRBilling || userData.business?.isQRBilling || false;
        } else {
          console.warn('⚠️ Failed to refetch user data, using existing data');
          isQRUser = (user?.isQRBilling || user?.business?.isQRBilling) === true;
          const storedUserData = JSON.parse(localStorage.getItem('user') || '{}');
          storedUserData.isOnboarded = true;
          localStorage.setItem('user', JSON.stringify(storedUserData));
          setUser(storedUserData);
        }
      } catch (error) {
        console.error('Failed to finalize onboarding:', error);
        isQRUser = (user?.isQRBilling || user?.business?.isQRBilling) === true;
      }
    }

    setOnboardingStep(0);
    setFromOnboarding(false);
    // Setup is over; the screen it ended on must not be restored next load.
    clearPersistedRouteState();

    const defaultTarget = isQRUser ? 'qr-owner-dashboard' : 'overview';
    const finalTarget = target || defaultTarget;
    if (showSuccessScreen) {
      setOnboardingSuccessTarget(finalTarget);
      // Transition screen, then the overview/dashboard.
      setScreen('onboarding-success');
    } else {
      setOnboardingSuccessTarget(null);
      setScreen(finalTarget);
    }
  }, [user, setUser]);

  // ── Sales Name Check (spec §10.4) ──────────────────────────────────────────
  // While a business is onboarding, an in-progress check makes the server refuse
  // Ingredient Reconciliation, opening stock and Closing Stock (409
  // SALES_NAME_CHECK_IN_PROGRESS). These keep the owner on the check instead of
  // opening a page the server would refuse. A failed status read lets the
  // navigation through: the server gate still holds and the page shows its
  // blocked panel.
  const openSalesNameCheckForOnboarding = () => {
    setFromOnboarding(true);
    trackedSetScreen('sales-name-check', { mode: 'onboarding' });
  };

  const checkSalesNameCheckGate = async () => {
    if (!isUserInOnboardingAccount) return true;
    const status = await getSalesNameCheckStatus();
    if (status.ok && status.state === 'in_progress' && status.mode === 'onboarding' && status.isOnboarding !== false) {
      openSalesNameCheckForOnboarding();
      window.alert('Sales Name Check is in progress — finish it or set it aside first.');
      return false;
    }
    return true;
  };

  // The same check on arrival, for the ways in that no handler above sees:
  // Inventory's own Back arrow and Closing Stock buttons, and a resume.
  useEffect(() => {
    if (!isAuthenticated || !isUserInOnboardingAccount) return undefined;
    if (screen !== 'ingredient-reconciliation' && screen !== 'inventory' && screen !== 'closing-stock') return undefined;
    let cancelled = false;
    getSalesNameCheckStatus().then((status) => {
      if (cancelled) return;
      if (status.ok && status.state === 'in_progress' && status.mode === 'onboarding' && status.isOnboarding !== false) {
        setFromOnboarding(true);
        setPreviousScreen(screen);
        setScreenParams({ mode: 'onboarding' });
        setScreen('sales-name-check');
        window.alert('Sales Name Check is in progress — finish it or set it aside first.');
      }
    });
    return () => { cancelled = true; };
  }, [screen, isAuthenticated, isUserInOnboardingAccount]);

  // Helper function to determine if navigation pane should be shown
  // Enabled for all Business IDs so navigation and subscription tools remain accessible
  // The floating navigation menu is a local-development tool only. It is hidden
  // for every business on any deployed host (invexis.co.in, Amplify previews) and
  // in the native app, whose WebView also reports the hostname "localhost".
  const shouldShowNavigation = () => isLocalDevHost();

  // Initialize app and handle authentication/onboarding on mount
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      CapacitorUpdater.notifyAppReady().catch((error) => {
        console.error('Failed to notify Capgo app ready:', error);
      });
    }

    // Get URL parameters and path once at the beginning
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const path = window.location.pathname;
    
    // PRIORITY 1: Check for password reset token or email verification in URL (must be first!)
    if (token) {
      // Check if this is email verification based on path (handle trailing slash from CDN)
      const normalizedPath = path.replace(/\/$/, '');
      if (normalizedPath === '/verify-email') {
        setResetToken(token);
        setScreen('email-verification');
        // Clear token from URL for security
        window.history.replaceState({}, document.title, window.location.pathname);
        return; // Stop here - don't check authentication or anything else
      } else {
        // Default to password reset
        setResetToken(token);
        setScreen('reset-password');
        // Clear token from URL for security
        window.history.replaceState({}, document.title, window.location.pathname);
        return; // Stop here - don't check authentication or anything else
      }
    }

    // PRIORITY 2: Check for policy pages
    if (path === '/Terms&Conditions' || path === '/terms-conditions') {
       setScreen('terms');
       return;
    }
    if (path === '/privacy_policy') {
       setScreen('privacy');
       return;
    }
    if (path === '/refund_policy') {
       setScreen('refund');
       return;
    }

    // Handle hash-based routing for customer menu
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/menu')) {
        // Check if it's just #/menu (without table info) - use for preview
        if (hash === '#/menu' || hash === '#/menu/') {
          setHashRoute({ type: 'customer-menu-preview', businessId: null, tableNumber: null });
          setScreen('customer-menu-preview');
          return;
        }
        // Extract businessId and tableNumber from URL
        const match = hash.match(/#\/menu\/(\d+)\/([\w-]+)/);
        if (match) {
          const [, businessId, tableNumber] = match;
          setHashRoute({ type: 'customer-menu', businessId, tableNumber });
          setScreen('customer-menu');
          return;
        }
      }
      setHashRoute(null);
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Check on mount

    const initialize = async () => {
      // Log startup information
      logStartupInfo();

      // Check database status after a short delay to ensure backend is ready
      await new Promise(resolve => setTimeout(resolve, 1000));
      await checkDatabaseStatus();

      const clearStoredRouteState = () => {
        sessionStorage.removeItem(SESSION_SCREEN_STATE_KEY);
        try {
          const currentHistoryState = (window.history && typeof window.history.state === 'object' && window.history.state)
            ? window.history.state
            : {};
          if (currentHistoryState && currentHistoryState[HISTORY_ROUTE_STATE_KEY]) {
            const { [HISTORY_ROUTE_STATE_KEY]: _hiddenRouteState, ...rest } = currentHistoryState;
            window.history.replaceState(rest, document.title, window.location.href);
          }
        } catch (error) {
          console.warn('Failed to clear hidden route state from history:', error);
        }
      };

      const shouldUseNormalFlowForRapidRefresh = () => {
        try {
          const navEntries = (typeof window !== 'undefined' && window.performance && window.performance.getEntriesByType)
            ? window.performance.getEntriesByType('navigation')
            : [];
          const navigationType = Array.isArray(navEntries) && navEntries.length > 0
            ? navEntries[0].type
            : null;

          // Count only browser reloads; fresh navigations should behave normally.
          if (navigationType !== 'reload') {
            sessionStorage.removeItem(RAPID_REFRESH_GUARD_KEY);
            return false;
          }

          const now = Date.now();
          let previousCount = 0;
          let previousReloadAt = 0;

          const guardRaw = sessionStorage.getItem(RAPID_REFRESH_GUARD_KEY);
          if (guardRaw) {
            const guardParsed = JSON.parse(guardRaw);
            previousCount = Number(guardParsed?.count) || 0;
            previousReloadAt = Number(guardParsed?.lastReloadAt) || 0;
          }

          const withinWindow = previousReloadAt > 0 && (now - previousReloadAt) <= RAPID_REFRESH_WINDOW_MS;
          const nextCount = withinWindow ? previousCount + 1 : 1;

          sessionStorage.setItem(RAPID_REFRESH_GUARD_KEY, JSON.stringify({
            count: nextCount,
            lastReloadAt: now
          }));

          if (nextCount >= RAPID_REFRESH_LIMIT) {
            clearStoredRouteState();
            return true;
          }

          return false;
        } catch (error) {
          console.warn('Failed to evaluate rapid refresh guard:', error);
          return false;
        }
      };

      // A browser reload is counted ONCE per page load. This effect re-runs when
      // `user` changes (W-5 adopts the server's onboarded user) and twice in
      // development (StrictMode); each run used to count as another F5 and, at
      // the limit, wiped the restored screen and sent the owner to the dashboard.
      if (rapidRefreshDecisionRef.current === null) {
        rapidRefreshDecisionRef.current = shouldUseNormalFlowForRapidRefresh();
      }
      const bypassRestoreDueToRapidRefresh = rapidRefreshDecisionRef.current;

      const readStoredRouteState = () => {
        try {
          const historyState = window.history?.state;
          const hiddenRouteState = historyState?.[HISTORY_ROUTE_STATE_KEY];
          if (hiddenRouteState && typeof hiddenRouteState === 'object') {
            return hiddenRouteState;
          }
        } catch (error) {
          console.warn('Failed to read route state from history:', error);
        }

        try {
          const raw = sessionStorage.getItem(SESSION_SCREEN_STATE_KEY);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
          console.warn('Failed to read route state from session storage:', error);
          return null;
        }
      };

      const restoreLastScreenFromSession = ({ qrEnabled = true } = {}) => {
        try {
          if (bypassRestoreDueToRapidRefresh) return false;

          const parsed = readStoredRouteState();
          if (!parsed?.screen) return false;
          if (NON_PERSISTABLE_SCREENS.has(parsed.screen)) return false;
          if (IS_PRODUCTION_BUILD && isLocalOnlyReportScreen(parsed.screen)) {
            clearStoredRouteState();
            return false;
          }

          const storedBusinessId = parsed.businessId != null ? String(parsed.businessId) : null;
          const activeBusinessId = localStorage.getItem('businessId');
          if (storedBusinessId && activeBusinessId && storedBusinessId !== String(activeBusinessId)) {
            return false;
          }

          const storedUserId = parsed.userId != null ? String(parsed.userId) : null;
          const activeUserIdValue = user?.id ?? user?.user_id;
          const activeUserId = activeUserIdValue != null ? String(activeUserIdValue) : null;
          if (storedUserId && activeUserId && storedUserId !== activeUserId) {
            return false;
          }

          if (!qrEnabled && parsed.screen === 'qr-owner-dashboard') {
            return false;
          }

          setScreenParams(parsed.params && typeof parsed.params === 'object' ? parsed.params : {});
          setScreen(parsed.screen);
          return true;
        } catch (error) {
          console.warn('Failed to restore screen from session storage:', error);
          return false;
        }
      };

      // Handle authenticated users
      if (isAuthenticated && user) {
        // If user is already on a specific screen (like onboarding), don't override it
        const protectedScreens = ['onboarding', 'onboarding-success', 'terms', 'privacy', 'refund'];
        if (protectedScreens.includes(screen)) {
          return;
        }

        const returnScreen = sessionStorage.getItem('invexis:return_screen_after_external');
        if (returnScreen) {
          sessionStorage.removeItem('invexis:return_screen_after_external');
          if (returnScreen === 'qr-owner-dashboard') {
            setScreen('qr-owner-dashboard');
            return;
          }
        }

        // Check if user is an invited user (not the business owner)
        // We use the explicit isOwner flag from the backend to ensure custom domain owners aren't bypassed
        const isInvitedUser = user.isOwner === false;
        
        // Invited users skip onboarding and go directly to their dashboard
        if (isInvitedUser) {
          if (restoreLastScreenFromSession({ qrEnabled: false })) {
            return;
          }
          setScreen('dashboard');
          return;
        }

        // Check backend user status to ensure DB updates (like onboarding reset) are respected
        let qrEnabled = (user?.isQRBilling || user?.business?.isQRBilling) === true;
        let isUserOnboarded = false;

        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await fetch(`${API_URL}/auth/user`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const data = await response.json();
              qrEnabled = data.isQRBilling || data.business?.isQRBilling || false;
              const backendOnboarded = parseBooleanLike(data.is_onboarded ?? data.isOnboarded ?? data.business?.is_onboarded ?? data.business?.isOnboarded);
              
              if (backendOnboarded === false) {
                // Backend says user needs onboarding — overwrite stale storage and state
                isUserOnboarded = false;
                clearPersistedRouteState();
                data.isOnboarded = false;
                localStorage.setItem('user', JSON.stringify(data));
                // setUser ONLY when the in-memory copy disagrees. `user` is a dependency of
                // this effect and setUser always hands it a new object, so an unconditional
                // call re-ran this effect forever: every ~1-2s it re-fetched /auth/user and
                // /auth/onboarding-progress and forced the screen back to the step's screen,
                // which is why Inventory's back arrow could never reach Ingredient
                // Reconciliation. Once the flag is false in state there is nothing to correct.
                if (parseBooleanLike(user?.isOnboarded ?? user?.is_onboarded) !== false) {
                  setUser(data);
                }
              } else if (backendOnboarded === true) {
                isUserOnboarded = true;
                data.isOnboarded = true;
                localStorage.setItem('user', JSON.stringify(data));
                // W-5: the in-memory user may still be a stale "not onboarded" copy
                // (onboarding finished on another device). Replace it, or this whole
                // session keeps the footer hidden and Item Map in mapping mode. Only
                // when they disagree — the same no-loop rule as the branch above.
                if (shouldAdoptOnboardedUser(user, true)) {
                  setUser(data);
                }
              } else {
                isUserOnboarded = parseBooleanLike(user?.isOnboarded ?? user?.is_onboarded) ?? false;
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ Failed to fetch user status on refresh:', error);
          isUserOnboarded = parseBooleanLike(user?.isOnboarded ?? user?.is_onboarded) ?? false;
        }

        // If fully onboarded, check MOG status before allowing access
        if (isUserOnboarded) {
          // Safety: fully onboarded users should never stay in onboarding-only UI mode.
          setFromOnboarding(false);
          
          // Check for pending complimentary items (MOG)
          try {
            const businessId = user.businessId || localStorage.getItem('businessId');
            const mogResponse = await fetch(`${API_URL}/complimentary-tracking/mog-status?businessId=${businessId}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (mogResponse.ok) {
              const mogData = await mogResponse.json();
              if (mogData.success && mogData.mogBlocked) {
                setScreen('complimentary-finalization');
                return;
              }
            }
          } catch (mogError) {
            console.warn('⚠️ MOG check failed, allowing access:', mogError);
          }

          if (restoreLastScreenFromSession({ qrEnabled })) {
            return;
          }

          setScreen(qrEnabled ? 'qr-owner-dashboard' : 'dashboard');
          return;
        }

        // User needs to complete onboarding - clear any stale dashboard session state
        clearPersistedRouteState();

        // User needs to complete onboarding - check for saved progress
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setScreen('onboarding');
            setOnboardingStep(1);
            return;
          }
          
          const response = await fetch(`${API_URL}/auth/onboarding-progress`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const savedStep = Number(data.onboardingStep) || 0;

            // EVERY STEP ALREADY DONE.
            //
            // The server re-derives each step from the business's own data, so
            // this fires for an account whose setup was completed by another
            // route — seeded recipes, a count filed from the Closing Stock
            // page — and was only ever waiting on a counter to catch up.
            // Finish it properly and hand over the dashboard.
            if (data.allComplete === true) {
              console.log('✅ All onboarding steps satisfied by data — completing setup');
              await completeOnboarding({ showSuccessScreen: false });
              return;
            }

            if (savedStep > 0 && savedStep <= 4) {
              // Resume from the first step whose work is genuinely missing.
              setOnboardingStep(savedStep);

              // Use QR billing status from backend (single source of truth)
              const isQRUser = data.isQRBilling === true;

              if (Array.isArray(data.steps)) {
                const outstanding = data.steps.filter((s) => !s.complete);
                console.log(
                  `📋 Onboarding resumes at step ${savedStep}. Outstanding: ` +
                  outstanding.map((s) => `${s.step} ${s.label} (${s.detail})`).join('; ')
                );
              }

              if (savedStep === 1) {
                // Step 1: Show QR billing onboarding or regular itemmap
                if (isQRUser) {
                  setScreen('qr-billing-onboarding');
                } else {
                  setScreen('onboarding');
                }
              }
              else if (savedStep === 2) {
                if (isQRUser) {
                  // QR onboarding step-2 currently continues in inventory calculator flow.
                  setFromOnboarding(true);
                  setScreen('inventory-calculator');
                } else if (data.nextScreen === 'sales-name-check') {
                  // Spec §10.3/§10.4: every recipe is confirmed and the check is not
                  // started or still in progress — resume on it, in onboarding mode.
                  setFromOnboarding(true);
                  setSalesNameCheckBackTo('itemmap');
                  setScreenParams({ mode: 'onboarding' });
                  setScreen('sales-name-check');
                } else {
                  setScreen(data.nextScreen || 'verification');
                }
              }
              else if (savedStep === 3) {
                setFromOnboarding(true); // Ensure fromOnboarding is set for step 3
                setScreen('inventory');
              }
              else if (savedStep === 4) {
                // Step 4: Resume directly at onboarding TSR screen
                setScreen('todays-sales-report');
              }
            } else {
              // BUG FIX: If user is NOT onboarded (reached here via outer if), they MUST NOT go to dashboard.
              // If step is 0 (not started) or invalid, force Step 1.
              setScreen('onboarding');
              setOnboardingStep(1);
            }
          } else if (response.status === 404) {
            // Endpoint not found - do NOT assume completion; start onboarding
            setScreen('onboarding');
            setOnboardingStep(1);
          } else {
            // API call failed with other error - start onboarding rather than forcing dashboard
            console.warn('⚠️ Failed to load onboarding progress - starting onboarding');
            setScreen('onboarding');
            setOnboardingStep(1);
          }
        } catch (error) {
          console.error('Failed to load onboarding progress:', error);
          // On error, start onboarding from step 1 to avoid unwanted dashboard redirect
          setScreen('onboarding');
          setOnboardingStep(1);
        }
      }
    };

    initialize();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isAuthenticated, user]);

  // TSR lock check callback (missed yesterday TSR) with grace period
  const checkTsrLock = useCallback(async () => {
    if (!isAuthenticated) return;
    
    // Skip lock checks during onboarding
    if (onboardingStep > 0) {
      return;
    }

    const API_BASE = API_URL || 'http://localhost:5000/api';
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) return;

      const response = await fetch(`${API_BASE}/operations/today-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-business-id': businessId
        }
      });

      const result = await response.json();
      if (result?.success) {
        // The filing date is needed on EVERY screen — most of all on the TSR
        // screen itself, which is where it is consumed. This used to sit below
        // an early return that skipped the fetch entirely on that screen, so a
        // session restored straight onto the TSR page never learned the date.
        setSuggestedTsrDate(result.data?.suggestedTsrDate || null);
        setYesterdayDate(result.data?.previousDayDate || null);
        setYesterdayWastageRecorded(result.data?.previousDayWastageRecorded === true);

        const missed = result.data?.previousDayTsrMissed === true;

        // If lock just transitioned from active -> cleared, play unlock animation once.
        if (!missed && wasTsrLockActiveRef.current) {
          setTsrUnlockAnimating(true);
        }

        if (missed) {
          setTsrUnlockAnimating(false);
        }

        setTsrLockActive(missed);
        wasTsrLockActiveRef.current = missed;
      }
    } catch (err) {
      console.warn('TSR lock check failed:', err.message);
    }
  }, [isAuthenticated, screen, onboardingStep]);

  // The lock is re-checked when it can actually have changed, not every 30s.
  //
  // This ran on every screen, for every signed-in user, all day — and asking
  // /operations/today-status is not even a read: it creates today's
  // DailyOperations row if it is missing. At 30s that was ~2,900 requests a day
  // per open tab, which on its own kept the Neon compute from ever suspending.
  //
  // Only three things can change the answer:
  //   - submitting the sales report, which is a write and says so
  //   - the clock reaching IST midnight (which day counts as "yesterday") or
  //     02:00 IST (the 26-hour grace in routes/dailyOperations.js expiring)
  //   - someone doing it on another device, which is caught on returning here
  //
  // Navigation re-runs this effect too (checkTsrLock closes over `screen`), so
  // moving around the app re-checks as it always did.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (onboardingStep > 0) return undefined;

    checkTsrLock();

    const recheck = () => { checkTsrLock(); };
    const stopListening = onDataChanged(recheck);
    const stopResuming = onScreenResumed(recheck);
    const stopSchedule = onIstHours([0, 2], recheck);
    window.addEventListener('daily-operations:refresh', recheck);

    return () => {
      stopListening();
      stopResuming();
      stopSchedule();
      window.removeEventListener('daily-operations:refresh', recheck);
    };
  }, [isAuthenticated, onboardingStep, checkTsrLock]);

  useEffect(() => {
    if (isAuthenticated) return;
    setTsrLockActive(false);
    setTsrUnlockAnimating(false);
    wasTsrLockActiveRef.current = false;
  }, [isAuthenticated]);

  // Separate effect to handle QR user routing when user data loads
  useEffect(() => {
    // Only run if user is authenticated, fully onboarded, and on core dashboard
    // Removed QR user redirect - all users now use main dashboard
  }, [isAuthenticated, user, screen]);

  // Guard local-only report screens in production, even if opened from stale state.
  useEffect(() => {
    if (!IS_PRODUCTION_BUILD || !isAuthenticated) {
      return;
    }
    if (!isLocalOnlyReportScreen(screen)) {
      return;
    }

    setScreenParams({});
    setScreen('dashboard');
  }, [isAuthenticated, screen]);

  // Toggle global CSS scope class on body depending on screen
  useEffect(() => {
    const shouldApplyGlobal = !(isAuthFlow || isOnboarding || isMappingFlow);
    const body = document.body;
    if (shouldApplyGlobal) {
      body.classList.add('app-global');
    } else {
      body.classList.remove('app-global');
    }
  }, [isAuthFlow, isOnboarding, isMappingFlow]);

  useEffect(() => {
    const body = document.body;
    const isFullBleedScreen = ['recipe-library', 'recipe-books', 'leakage-eom', 'sales-eom', 'wastage-eom', 'expense-eom', 'banquet-eom', 'recipe-library-review', 'bean-there-review', 'discover-filter', 'structured-recipe-book'].includes(screen);
    if (isFullBleedScreen) {
      body.classList.add('sr1530-fullbleed');
    } else {
      body.classList.remove('sr1530-fullbleed');
    }

    return () => {
      body.classList.remove('sr1530-fullbleed');
    };
  }, [screen]);

  // State for vendor category filtering
  const [vendorCategoryFilter, setVendorCategoryFilter] = useState('All');

  // Ensure SPA screen changes start from top.
  // Without this, the browser can retain previous scroll position, causing Inventory/Reports
  // to sometimes appear opened from the middle.
  useEffect(() => {
    // Run after DOM updates to avoid layout race.
    requestAnimationFrame(() => scrollToTop());
  }, [screen]);

  // Vendor selection handlers
  const handleNavigateToVendors = (category = 'All') => {
    setVendorCategoryFilter(category);
    setIsSelectingVendor(true);
    setScreen('vendors');
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setIsSelectingVendor(false);
    setScreen('create-reorder');
  };

  const handleClearSelectedVendor = () => {
    setSelectedVendor(null);
  };

  const handleVendorManagementBack = () => {
    if (isSelectingVendor) {
      setIsSelectingVendor(false);
      setScreen('create-reorder');
    } else if (vendorOrigin === 'settings') {
      setVendorOrigin(null);
      setScreen('settings');
    } else {
      setScreen('dashboard');
    }
  };

  // Persist the last meaningful app screen for refresh recovery in the same tab.
  // sessionStorage is tab-scoped, so a fresh tab/window naturally follows normal flow.
  useEffect(() => {
    const hasAuthToken = Boolean(localStorage.getItem('token'));

    if (!isAuthenticated) {
      // During refresh bootstrap, AuthContext may be false briefly even with a valid token.
      // Clear only when token is truly absent (actual logout/new unauth session).
      if (!hasAuthToken) {
        sessionStorage.removeItem(SESSION_SCREEN_STATE_KEY);
        try {
          const currentHistoryState = (window.history && typeof window.history.state === 'object' && window.history.state)
            ? window.history.state
            : {};
          if (currentHistoryState && currentHistoryState[HISTORY_ROUTE_STATE_KEY]) {
            const { [HISTORY_ROUTE_STATE_KEY]: _hiddenRouteState, ...rest } = currentHistoryState;
            window.history.replaceState(rest, document.title, window.location.href);
          }
        } catch (error) {
          console.warn('Failed to clear hidden route state from history:', error);
        }
      }
      return;
    }

    if (!screen || NON_PERSISTABLE_SCREENS.has(screen) || (IS_PRODUCTION_BUILD && isLocalOnlyReportScreen(screen))) {
      return;
    }

    const stateToPersist = {
      screen,
      params: screenParams && typeof screenParams === 'object' ? screenParams : {},
      userId: user?.id ?? user?.user_id ?? null,
      businessId: localStorage.getItem('businessId') || null,
      savedAt: Date.now()
    };

    sessionStorage.setItem(SESSION_SCREEN_STATE_KEY, JSON.stringify(stateToPersist));

    try {
      const currentHistoryState = (window.history && typeof window.history.state === 'object' && window.history.state)
        ? window.history.state
        : {};
      const nextHistoryState = {
        ...currentHistoryState,
        [HISTORY_ROUTE_STATE_KEY]: stateToPersist
      };
      // Keep URL unchanged; route state lives in browser history state.
      window.history.replaceState(nextHistoryState, document.title, window.location.href);
    } catch (error) {
      console.warn('Failed to persist hidden route state in history:', error);
    }
  }, [isAuthenticated, screen, screenParams, user]);

  const isSubscriptionLocked = isAuthenticated
    && user
    && user.subscription === false
    && user.isTrialActive === false
    && !isSubscriptionLockExemptBusiness(user.businessId ?? user.business_id ?? localStorage.getItem('businessId'));
  const isTsrUnlockedScreen =
    screen === 'todays-sales-report'
    || screen === 'usage'
    || screen === 'stock-out'
    || screen === 'inventory-stock-out';

  return (
    <div className="App">
    {/* Subscription Lock Modal - Blocks ALL access when subscription is inactive and trial expired */}
    {isSubscriptionLocked && !isAuthFlow && (
      <SubscriptionLockModal email={user?.email} />
    )}

    {/* Plan ending soon (owners, from 7 days before the end date). Keyed on the user so a
        sign-in as someone else asks again. */}
    {isAuthenticated && !isAuthFlow && !isSubscriptionLocked && user && user.isOwner === true && (
      <SubscriptionEndingBanner key={user.userId || user.user_id || user.id || 'owner'} />
    )}

    {/* TSR Lock Modal - Shown when previous day's TSR is missed (blocks all access EXCEPT wastage and TSR submission) */}
    {!isSubscriptionLocked && !isAuthFlow && isAuthenticated && (tsrLockActive || tsrUnlockAnimating) && !isTsrUnlockedScreen && (
      <TSRLockModal 
        onNavigateToWastage={() => {
          trackedSetScreen('inventory-stock-out', { recoveryDate: yesterdayDate });
        }}
        onNavigateToTSR={() => {
          trackedSetScreen('todays-sales-report');
        }}
        yesterdayDate={yesterdayDate}
        completedSteps={yesterdayWastageRecorded ? [1] : []}
        forceUnlocked={tsrUnlockAnimating}
        onUnlockAnimationComplete={() => setTsrUnlockAnimating(false)}
      />
    )}

    {/* Top Navigation - show after auth across all screens */}
  {shouldShowNavigation() && (isSubscriptionLocked || !tsrLockActive || isTsrUnlockedScreen) && !isAuthFlow && (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Navigation currentScreen={screen} goTo={trackedSetScreen} tsrLockActive={tsrLockActive} allowedScreens={['todays-sales-report', 'usage', 'stock-out', 'inventory-stock-out']} subscriptionLocked={isSubscriptionLocked} />
        </div>
      )}

  {(!tsrLockActive || isTsrUnlockedScreen) && (
  <div className="app-main">
        {screen === 'login' && (
          <div>
            <LoginScreen
              isAuthenticated={isAuthenticated}
              onLogin={async (userData) => {
                // Login handled by AuthContext now
                
                // Check if onboarding is completed
                if (userData?.isOnboarded) {
                  setScreen('dashboard');
                } else {
                  // Don't set screen here - let the useEffect (line 148) fetch saved progress
                  // and determine the correct onboarding screen (Step 1, 2, 3, or 4)
                }
              }}
              onForgotPassword={() => setScreen('forgot')}
              onSignUp={() => setScreen('signup')}
            />
          </div>
        )}
        {screen === 'onboarding' && (
          <OnboardingPage onContinue={async (data) => { 
            // Check if QR billing was selected
            if (data?.isQRBilling) {
              // QR setup is its own detour and finishes Step 1 itself.
              await advanceOnboardingToStep(1);
              setQrOnboardingRequired(true);
              setScreen('qr-billing-onboarding');
              return;
            }

            await advanceOnboardingToStep(2);
            setScreen('itemmap');
          }} />
        )}
        {screen === 'signup' && (
          <SignUpScreen
            onBack={() => setScreen('login')}
            onSignUpComplete={() => {
              setScreen('login');
            }}
          />
        )}
        {screen === 'qr-billing-onboarding' && (
          <QRBillingOnboardingComplete
            isInitialSetup={true}
            onBack={async () => {
              // Navigate back to OnboardingPage (pic 1)
              await advanceOnboardingToStep(1);
              setScreen('onboarding');
            }}
            onComplete={async () => {
              await advanceOnboardingToStep(2);
              setQrOnboardingRequired(false);
              setFromOnboarding(true);
              setScreen('inventory-calculator');
            }}
          />
        )}
        {screen === 'qr-billing-onboarding-edit' && (
          <QRBillingOnboardingComplete
            isInitialSetup={false}
            startFromBeginning={false}
            onComplete={() => {
              setScreen('qr-owner-dashboard');
            }}
          />
        )}
        {screen === 'forgot' && (
          <ForgotPasswordScreen
            onBack={() => setScreen('login')}
            onSendReset={(email) => {
              console.log('Reset link sent to:', email);
            }}
          />
        )}
        {screen === 'reset-password' && (
          <ResetPasswordScreen
            token={resetToken}
            onBack={() => {
              setResetToken(null);
              setScreen('login');
            }}
            onResetSuccess={(email) => {
              setResetToken(null);
              setScreen('login');
            }}
          />
        )}
        {screen === 'email-verification' && (
          <EmailVerificationScreen
            token={resetToken}
            onBack={() => {
              setResetToken(null);
              setScreen('login');
            }}
            onVerifySuccess={(email) => {
              setResetToken(null);
              setScreen('login');
            }}
          />
        )}
        {screen === 'test' && <TestUnitMapping />}
        {screen === 'service-test' && <ServiceTester goTo={setScreen} />}
        {screen === 'test-medium-features' && <TestMediumFeatures />}
        {/* Deprecated Unit Mapping screens
        {screen === 'map' && <MapStep1 goTo={setScreen} />}
        {screen === 'map1' && (
          <KitchenUnitsStep 
            goTo={setScreen} 
            mappingOrigin={mappingOrigin}
            clearMappingOrigin={() => setMappingOrigin(null)}
          />
        )}
        {screen === 'map2' && <SupplierConversions goTo={setScreen} />}
        {screen === 'map3' && <UnitMappingComplete goTo={(nextScreen) => {
          setScreen(nextScreen);
        }} />}
        */}
        {screen === 'initial-recovery' && (
          <InitialItemRecoveryPage 
            onComplete={async () => {
              await advanceOnboardingToStep(4);
              setScreen('todays-sales-report');
            }}
          />
        )}
        {screen === 'itemmap' && <RecipeListScreen 
          goTo={async (nextScreen, params) => {
          // After itemmap is complete, the optional Sales Name Check comes
          // before ingredient reconciliation and Stock In.
          if (onboardingStep === 2 || fromOnboarding || isUserInOnboardingAccount) {
            setFromOnboarding(true);
            setSalesNameCheckBackTo('itemmap');
            // Explicit mode: a stale { mode: 'reopen' } from Settings must never carry over.
            trackedSetScreen('sales-name-check', { mode: 'onboarding' });
          } else {
            // params forwarded, not dropped: the calibration badge on a recipe
            // line links to that ingredient's history, and a wrapper that takes
            // only a screen name would land on the wrong one.
            trackedSetScreen(nextScreen, params || {});
          }
        }}
        onBack={(onboardingStep === 2 || fromOnboarding || isUserInOnboardingAccount) ? async () => {
          await advanceOnboardingToStep(1);
          setScreen('onboarding');
        } : undefined}
        previousScreen={previousScreen} isOnboarding={onboardingStep === 2 || fromOnboarding || isUserInOnboardingAccount} />}
        {screen === 'qr-itemmap' && (
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <QRitemmap 
              goTo={async (nextScreen) => {
              // After itemmap is complete, the optional Sales Name Check comes
              // before ingredient reconciliation and Stock In.
              if (onboardingStep === 2 || fromOnboarding || isUserInOnboardingAccount) {
                setFromOnboarding(true);
                setSalesNameCheckBackTo('qr-itemmap');
                trackedSetScreen('sales-name-check', { mode: 'onboarding' });
              } else {
                setScreen(nextScreen);
              }
            }} />
          </div>
        )}
        {screen === 'sales-name-check' && (
          <SalesNameCheck
            mode={(screenParams && screenParams.mode) || 'onboarding'}
            onBack={() => {
              if (screenParams && screenParams.mode === 'reopen') {
                // Reopened from Settings (spec §10.5): back to Settings, Inventory section open.
                const backTo = screenParams.backTo || 'settings';
                if (backTo === 'settings') {
                  trackedSetScreen('settings', { expandSection: 'inventory' });
                } else {
                  setScreen(backTo);
                }
              } else {
                setScreen(salesNameCheckBackTo);
              }
            }}
            onContinue={async () => {
              if (!(await checkSalesNameCheckGate())) return;
              trackedSetScreen('ingredient-reconciliation', { backTo: 'sales-name-check' });
            }}
            onOpenItemMap={() => {
              // Item Map has no per-dish deep link: the added dishes are its orange
              // "Added from sales" cards. fromOnboarding is left as it is, so an
              // onboarding owner lands in the onboarding Item Map.
              setScreen(salesNameCheckBackTo === 'qr-itemmap' ? 'qr-itemmap' : 'itemmap');
            }}
          />
        )}
        {screen === 'bill-memory' && (
          <BillMemoryPage
            onBack={() => {
              if (screenParams && screenParams.backTo === 'settings') {
                trackedSetScreen('settings', { expandSection: 'inventory' });
              } else if (screenParams && screenParams.backTo) {
                setScreen(screenParams.backTo);
              } else {
                trackedSetScreen('settings', { expandSection: 'inventory' });
              }
            }}
          />
        )}
        {screen === 'ingredient-reconciliation' && (
          <IngredientReconciliationRegistry
            onOpenSalesNameCheck={openSalesNameCheckForOnboarding}
            onBack={() => {
              if (screenParams && screenParams.backTo === 'settings') {
                // Opened from Settings: return there with the Inventory section open.
                trackedSetScreen('settings', { expandSection: 'inventory' });
              } else if (screenParams && screenParams.backTo) {
                setScreen(screenParams.backTo);
              } else if (fromOnboarding || isUserInOnboardingAccount || (onboardingStep >= 1 && onboardingStep <= 3)) {
                setScreen('sales-name-check');
              } else {
                // A live business (onboardingStep 0) with nowhere recorded goes home, never into setup.
                setScreen('dashboard');
              }
            }}
            onSave={async () => {
              if (!(await checkSalesNameCheckGate())) return;
              if (onboardingStep === 2) {
                setScreen('verification');
              } else {
                setScreen('inventory');
              }
            }}
          />
        )}
        {screen === 'verification' && (
          <Verification
            onBack={() => {
              setScreen('ingredient-reconciliation');
            }}
            onContinue={async (payload) => {
              if (!(await checkSalesNameCheckGate())) return;
              localStorage.setItem('onboarding_verification_payload', JSON.stringify(payload));
              
              if (onboardingStep === 2) {
                await advanceOnboardingToStep(3);
                setFromOnboarding(true);
              }
              setScreen('inventory');
            }}
          />
        )}
  {screen === 'dashboard' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('dashboard')} goTo={setScreen}>
            <Dashboard goTo={trackedSetScreen} currentUser={user} previousScreen={previousScreen} />
          </ProtectedRoute>
        )}
        {screen === 'notifications' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('notifications')} goTo={setScreen}>
            <Notification goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'overview' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <InventoryOverview
              goTo={trackedSetScreen}
              previousScreen={previousScreen}
              fromOnboarding={fromOnboarding}
              isOnboarding={isUserInOnboardingAccount}
              onExitOnboarding={() => setFromOnboarding(false)}
            />
          </ProtectedRoute>
        )}
  {screen === 'inventory' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <>
              <LegacyUnitWarning apiBaseUrl={API_URL} />
              <InventoryOverview 
                goTo={trackedSetScreen} 
                initialTab="overview" 
                previousScreen={previousScreen}
                fromOnboarding={fromOnboarding}
                isOnboarding={isUserInOnboardingAccount && fromOnboarding}
                onExitOnboarding={() => setFromOnboarding(false)}
                onOpenSalesNameCheck={openSalesNameCheckForOnboarding}
                onOnboardingComplete={async () => {
                  // Closing stock is complete — finalize onboarding and navigate to Overview page.
                  await completeOnboarding();
                }}
              />
            </>
          </ProtectedRoute>
        )}
  {screen === 'inventory-stock-out' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <InventoryOverview 
              goTo={trackedSetScreen} 
              initialTab="stock-out" 
              initialStockOutSubTab="Waste" 
              previousScreen={previousScreen}
              tsrLockActive={tsrLockActive}
              recoveryDate={screenParams?.recoveryDate || (tsrLockActive ? yesterdayDate : null)}
              openWastageReasonModal={openWastageReasonModal}
              onWastageReasonModalOpened={() => setOpenWastageReasonModal(false)}
            />
          </ProtectedRoute>
        )}
  {screen === 'xyz' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <Xyz goTo={trackedSetScreen} previousScreen={previousScreen} />
          </ProtectedRoute>
        )}
  {(screen === 'recipe-library' || screen === 'recipe-books') && (
          <RecipeBookCatalog
            onSelectBook={(book) => {
              setSelectedRecipeBook(book);
              trackedSetScreen('recipe-library-review');
            }}
          />
        )}
  {(screen === 'recipe-library-review' || screen === 'bean-there-review' || screen === 'discover-filter') && (
          <RecipeLibraryReview goTo={trackedSetScreen} selectedBook={selectedRecipeBook} />
        )}
  {screen === 'structured-recipe-book' && (
          <StructuredRecipeBook goTo={trackedSetScreen} onBack={() => trackedSetScreen('dashboard')} />
        )}
  {screen === 'leakage' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <Leakage 
                goTo={trackedSetScreen} 
                ingredientId={screenParams.ingredientId}
                ingredientName={screenParams.ingredientName}
              />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-monthly' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <LeakageMonth goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-monthly-category' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <LeakageCategory goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-monthly-item' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <LeakageItemCard goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-monthly-history' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <LeakageHistory goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {/* Phase 0 (LEAKAGE_SIGNATURE_SPEC §12): the two screens that make a first
      run trustworthy, plus the weekly count sheet from §11. All three ask the
      owner for a fact rather than showing them one, so they sit outside the
      four report screens above. */}
  {screen === 'leakage-prep-yield' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <PrepYieldReview goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-mapping-integrity' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <MappingIntegrity goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-a-tier' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('closing-stock')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <ATierCount goTo={trackedSetScreen} screenParams={screenParams} />
            </React.Suspense>
          </ProtectedRoute>
        )}
  {screen === 'leakage-eom' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <LeakageEom goTo={setScreen} />
          </ProtectedRoute>
        )}
  {screen === 'sales-eom' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <SalesEom goTo={setScreen} />
          </ProtectedRoute>
        )}
  {screen === 'wastage-eom' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <WastageEom goTo={setScreen} />
          </ProtectedRoute>
        )}
  {screen === 'expense-eom' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <ExpenseEom goTo={setScreen} />
          </ProtectedRoute>
        )}
  {screen === 'inventory-calculator' && (
    <InventoryCalculator 
      goTo={async (nextScreen) => {
        // Moving to opening stock from the inventory calculator is Step 3.
        if (fromOnboarding && nextScreen === 'stock-in-scan') {
          await advanceOnboardingToStep(3);
          setScreen('inventory');
        } else {
          setScreen(nextScreen);
        }
      }}
      fromOnboarding={fromOnboarding} 
      onContinue={() => setFromOnboarding(true)}
    />
  )}
  {screen === 'stock-in-scan' && <InventoryOverview 
    goTo={trackedSetScreen} 
          initialTab="stock-in"
          previousScreen={previousScreen}
          fromOnboarding={fromOnboarding}
          onExitOnboarding={() => setFromOnboarding(false)}
          isOnboarding={isUserInOnboardingAccount && fromOnboarding}
          onOnboardingComplete={async () => {
            // Stock-in done during onboarding → complete onboarding and go to Overview.
            await completeOnboarding();
          }}
        />}

  {screen === 'onboarding-success' && (
    <OnboardingSuccessScreen
      durationMs={3000}
      onDone={() => {
        const target = onboardingSuccessTarget || 'overview';
        setOnboardingSuccessTarget(null);
        setScreen(target);
      }}
    />
  )}
  {screen === 'stock-in' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('stock-in')} goTo={setScreen}>
            <StockInForm 
              goTo={trackedSetScreen}
              onSuccess={async () => {
                // If during onboarding, complete onboarding and proceed to Overview
                if (onboardingStep === 3) {
                  await completeOnboarding();
                }
              }}
            />
          </ProtectedRoute>
        )}
  {screen === 'stock-out' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('stock-out')} goTo={setScreen}>
            <StockOutForm 
              goTo={trackedSetScreen} 
              previousScreen={previousScreen} 
              recoveryDate={screenParams?.recoveryDate || (tsrLockActive ? yesterdayDate : null)}
              // The day this wastage is FOR when no recovery flow fixed one. NOT
              // yesterdayDate: that is the lock's subject and is null for the whole
              // 00:00–02:00 grace window — exactly when a late-closing restaurant
              // files — so the form opened on the new calendar day and stamped last
              // night's wastage / NC on tomorrow. Same value, same reason, as the
              // TSR screen's targetDate below.
              suggestedDate={suggestedTsrDate}
              onLockStateChange={(lockState) => {
                setIsFooterLocked(lockState.isActive);
              }}
              openReasonModal={openWastageReasonModal}
              onReasonModalOpened={() => setOpenWastageReasonModal(false)}
            />
          </ProtectedRoute>
        )}
  {screen === 'usage' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('wastage')} goTo={setScreen}>
            <Usage goTo={trackedSetScreen} previousScreen={previousScreen} />
          </ProtectedRoute>
        )}
        {screen === 'todays-sales-report' && (
          onboardingStep === 4 ? (
            <OnboardingTSRWrapper 
              goTo={setScreen}
              // Step 4 is the last one — the first TSR is filed, so the setup
              // really is finished. This is the ONLY place completion happens.
              onComplete={completeOnboarding}
            />
          ) : (
            <TodaysSalesReport 
              goTo={trackedSetScreen} 
              previousScreen={previousScreen}
              fromLockModal={tsrLockActive}
              // The filing date, NOT the lock's subject. yesterdayDate is null
              // for the whole 00:00–02:00 grace window, which is exactly when a
              // late-closing restaurant files — so using it here booked last
              // night's sales onto today.
              targetDate={suggestedTsrDate}
              onBeforeNavigateToWastage={() => setOpenWastageReasonModal(true)}
              onRecoveryTsrCompleted={() => {
                setTsrLockActive(false);
                setTsrUnlockAnimating(true);
                wasTsrLockActiveRef.current = false;
              }}
            />
          )
        )}
        {screen === 'wastage-thresholds' && <WastageThresholds goTo={setScreen} />}
        {screen === 'wastage-threshold-approvals' && <WastageThresholdApprovals goTo={setScreen} />}
        {screen === 'test-approval-popup' && <TestApprovalPopup />}
        {screen === 'expiry-thresholds' && <ExpiryThresholdsComponent goTo={setScreen} />}
        {screen === 'ocr' && <OCRUpload goTo={setScreen} />}
        {/* QR billing onboarding is handled within the onboarding flow at line 263 */}
        {screen === 'qr-owner-dashboard' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('dashboard')} goTo={setScreen}>
            <OwnerApp 
              goTo={setScreen}
              onSwipeToCoreDashboard={() => {
                setScreen('dashboard');
              }}
            />
          </ProtectedRoute>
        )}
        {screen === 'qr-menu-management' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('menu')} goTo={setScreen}>
            <OwnerMenuManagement onBack={() => setScreen('qr-owner-dashboard')} goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'qr-analytics' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('analytics')} goTo={setScreen}>
            <OwnerAnalytics onBack={() => setScreen('qr-owner-dashboard')} />
          </ProtectedRoute>
        )}
        {screen === 'qr-tables' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('tables')} goTo={setScreen}>
            <QRTableManagement onBack={() => setScreen('qr-owner-dashboard')} />
          </ProtectedRoute>
        )}
        {screen === 'chef-dashboard' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('chef-dashboard')} goTo={setScreen}>
            <ChefPage goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'waiter-dashboard' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('orders')} goTo={setScreen}>
            <WaiterDashboard goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'waiter-pov' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('orders')} goTo={setScreen}>
            <WaiterDashboard goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'customer-menu' && hashRoute && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Customer Menu</h2>
            <p>Customer menu component will be implemented here</p>
          </div>
        )}
        {screen === 'customer-menu-preview' && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Customer Menu Preview</h2>
            <p>Customer menu preview component will be implemented here</p>
          </div>
        )}
        {screen === 'complimentary-finalization' && (
          <ComplimentaryRecipeFinalization 
            onComplete={() => setScreen('dashboard')}
            onBack={() => setScreen('dashboard')}
          />
        )}
        {screen === 'vendors' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('vendors')} goTo={setScreen}>
            <VendorManagement 
              onNavigateBack={handleVendorManagementBack}
              isSelectingMode={isSelectingVendor}
              onSelectVendor={handleVendorSelect}
              initialCategoryFilter={vendorCategoryFilter}
            />
          </ProtectedRoute>
        )}
  {screen === 'minimal-stock' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('inventory')} goTo={setScreen}>
            <MinimalStock goTo={setScreen} />
          </ProtectedRoute>
        )}
        {screen === 'create-reorder' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('vendors')} goTo={setScreen}>
            <CreateReorder 
              onNavigateBack={() => setScreen('minimal-stock')} 
              onNavigateToVendor={handleNavigateToVendors}
              selectedVendor={selectedVendor}
              onClearSelectedVendor={handleClearSelectedVendor}
              /*
                trackedSetScreen, not setScreen: the Kitchen Leakage banner on
                this screen navigates with a month ({ month: '2026-08' }), and a
                plain useState setter silently drops its second argument. The
                banner then advertised August's locked total and opened the
                CURRENT month, which has no closing count yet — so the one tap
                the banner exists for landed on "give closing stock".
              */
              goTo={trackedSetScreen}
            />
          </ProtectedRoute>
        )}
  {screen === 'reports' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <ReportsScreen goTo={trackedSetScreen} previousScreen={previousScreen} />
          </ProtectedRoute>
        )}
  {screen === 'sales-report' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('sales-report')} goTo={setScreen}>
            <SalesReport goTo={trackedSetScreen} previousScreen={previousScreen} />
          </ProtectedRoute>
        )}

        {screen === 'online-orders-report' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <OnlineOrdersReport goTo={trackedSetScreen} />
          </ProtectedRoute>
        )}

        {/* Banquet Orders. Lazy, like the leakage screens, so the chart and PDF
            builder are not in the main bundle for the businesses that do not
            have the lane. The Reports card that reaches it is flag-gated; the
            API behind it 404s regardless, so this route is inert without both. */}
        {screen === 'banquet-orders-report' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <BanquetOrdersReport goTo={trackedSetScreen} />
            </React.Suspense>
          </ProtectedRoute>
        )}

        {/* Banquet EOM. Its own report, not one of the EOM screens above: built from
            banquet records only. Reached from the ☰ menu's Banquet Reports group, which
            is flag-gated, and its API 404s for a business that is not Restaurant + Banquet. */}
        {screen === 'banquet-eom' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('banquet-eom')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <BanquetEom goTo={trackedSetScreen} />
            </React.Suspense>
          </ProtectedRoute>
        )}

        {/* Net Profit Analyzer: Settings → Financials & Profitability, and the Sales Report's
            Fixed Expense tab. screenParams carry the page, month, worker or item, and returnTo.
            'net-profit-close' and the retired 'net-profit-prototype' open the same module. */}
        {(screen === 'net-profit' || screen === 'net-profit-close' || screen === 'net-profit-prototype') && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('reports')} goTo={setScreen}>
            <React.Suspense fallback={null}>
              <NetProfitApp
                goTo={trackedSetScreen}
                screenParams={screen === 'net-profit-close' ? { ...(screenParams || {}), route: 'close' } : (screenParams || {})}
              />
            </React.Suspense>
          </ProtectedRoute>
        )}
        {screen === 'customization-mapping' && <CustomizationMapping goTo={setScreen} />}
        {/* FEATURE DISABLED: VID Manager - To re-enable, uncomment below */}
        {/* {screen === 'vid-manager' && <VirtualInventoryManager goTo={setScreen} />} */}
        {screen === 'expiry-control' && <ExpiryControlPanel goBack={() => setScreen('dashboard')} />}
        {screen === 'settings' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('settings')} goTo={setScreen}>
            <SettingsPage 
              goTo={setScreen} 
              currentUser={user}
              screenParams={screenParams}
              onOpenFromSettings={(next, extra) => {
                // Pages opened from Settings return to Settings (spec §10.4/§10.5);
                // Sales Name Check opens in reopen mode. `extra` names a page inside the
                // opened screen (the Net Profit Analyzer's Staff salaries).
                trackedSetScreen(next, next === 'sales-name-check'
                  ? { backTo: 'settings', mode: 'reopen' }
                  : { backTo: 'settings', ...(extra || {}) });
              }}
              onNavigateToUnits={() => {
                setMappingOrigin('settings');
                setScreen('itemmap');
              }}
              onNavigateToVendors={() => {
                setVendorOrigin('settings');
                setScreen('vendors');
              }}
            />
          </ProtectedRoute>
        )}
        {screen === 'structural-action-log' && (
          <ProtectedRoute requiredPermissions={getScreenPermissions('settings')} goTo={setScreen}>
            <StructuralActionLog
              variant="page"
              onBack={() => setScreen('settings')}
            />
          </ProtectedRoute>
        )}
        {screen === 'category-maintenance' && (
          <CategoryMaintenancePage goTo={setScreen} />
        )}
        {screen === 'closing-stock' && (
          <ClosingStockPage
            goTo={trackedSetScreen}
            screenParams={screenParams}
            isOnboarding={onboardingStep === 3}
            onOpenSalesNameCheck={openSalesNameCheckForOnboarding}
            onOnboardingComplete={async () => {
              await completeOnboarding();
            }}
          />
        )}
        {screen === 'closing-stock-check' && (
          <ClosingStockCheck
            onBack={() => {
              setScreen('closing-stock');
            }}
            onConfirm={async (payload) => {
              localStorage.setItem('onboarding_closing_stock_check_payload', JSON.stringify(payload));
              // Completing closing stock check completes onboarding setup
              await completeOnboarding();
            }}
          />
        )}

        {/* Policy Pages */}
        {screen === 'terms' && <TermsPage onBack={() => setScreen(isAuthenticated ? 'dashboard' : 'login')} />}
        {screen === 'privacy' && <PrivacyPolicyPage onBack={() => setScreen(isAuthenticated ? 'dashboard' : 'login')} />}
        {screen === 'refund' && <RefundPolicyPage onBack={() => setScreen(isAuthenticated ? 'dashboard' : 'login')} />}
      </div>
  )}

    {/* Fixed Footer Navigation across the project after login (hide inside QR Billing to avoid double footers) */}
  {(!tsrLockActive || screen === 'todays-sales-report' || screen === 'usage' || screen === 'stock-out' || screen === 'inventory-stock-out') &&
  !isAuthFlow && !isOnboarding && !isMappingFlow && 
  !isOnboardingTsrRecipeFlow &&
   !(fromOnboarding && (screen === 'itemmap' || screen === 'qr-itemmap' || screen === 'stock-in-scan' || screen === 'inventory-calculator' || screen === 'sales-name-check' || screen === 'ingredient-reconciliation' || screen === 'closing-stock' || screen === 'inventory' || screen === 'closing-stock-check')) &&
   screen !== 'qr-billing' && 
   screen !== 'qr-billing-onboarding' && 
   screen !== 'qr-billing-onboarding-edit' && 
   screen !== 'qr-owner-dashboard' && 
   screen !== 'chef-dashboard' &&
  screen !== 'waiter-dashboard' &&
  screen !== 'waiter-pov' &&
   
  screen !== 'qr-menu-management' && 
  screen !== 'qr-analytics' && 
  screen !== 'qr-tables' && 
  screen !== 'customer-menu' && 
  screen !== 'customer-menu-preview' && 
  screen !== 'terms' &&
  screen !== 'privacy' &&
  screen !== 'refund' &&
  screen !== 'leakage-eom' &&
  screen !== 'sales-eom' &&
  screen !== 'wastage-eom' &&
  screen !== 'expense-eom' &&
  screen !== 'banquet-eom' &&
  screen !== 'net-profit' &&
  screen !== 'net-profit-close' &&
  screen !== 'net-profit-prototype' &&
  screen !== 'recipe-library' &&
  screen !== 'recipe-books' &&
  screen !== 'recipe-library-review' &&
  screen !== 'bean-there-review' &&
  screen !== 'discover-filter' &&
  (
      <FooterNav
        current={screen}
        goTo={trackedSetScreen}
        isLocked={isFooterLocked}
        tsrLockActive={tsrLockActive}
        allowedScreens={['todays-sales-report', 'usage', 'stock-out', 'inventory-stock-out']}
      />
    )}
    </div>
  );
}

// Wrap App with TranslationProvider
const AppWithTranslations = () => (
  <TranslationProvider>
    <App />
  </TranslationProvider>
);

export default AppWithTranslations;
